import { useAuth, useClerk } from '@clerk/react'
import { Star } from 'lucide-react'
import { useCallback, useEffect, useRef, useState } from 'react'
import toast from 'react-hot-toast'
import { useLocation } from '#router'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { trpc } from '@/lib/trpc'

interface ForkStarButtonProps {
  sourceType: 'list' | 'topic' | 'subtopic' | 'chapter'
  sourceId: string
  className?: string
}

export function ForkStarButton({
  sourceType,
  sourceId,
  className,
}: ForkStarButtonProps) {
  const { isSignedIn } = useAuth()
  const { openSignIn } = useClerk()
  const { navigate } = useLocation()
  const utils = trpc.useUtils()
  const [open, setOpen] = useState(false)
  const [name, setName] = useState('')
  const prefilledRef = useRef(false)
  const defaultNameQuery = trpc.lists.getDefaultForkName.useQuery(
    { sourceType, sourceId },
    { enabled: open },
  )

  // Prefill the suggested name once per dialog-open, when it resolves. Two
  // guards, both needed:
  //  - the ref makes the fill happen at most once per open, so clearing the
  //    field to type a custom name doesn't immediately snap the default back in;
  //  - `name === ''` avoids overwriting text the user typed while the query was
  //    still loading (it's async and the input autofocuses on open).
  // The ref resets whenever the dialog closes, so the next open prefills again.
  useEffect(() => {
    if (!open) {
      prefilledRef.current = false
      return
    }
    if (!prefilledRef.current && defaultNameQuery.data?.name && name === '') {
      prefilledRef.current = true
      setName(defaultNameQuery.data.name)
    }
  }, [open, defaultNameQuery.data?.name, name])

  const forkMutation = trpc.lists.fork.useMutation({
    onSuccess: (data) => {
      toast.success(`Saved to "${data.list.name}"`)
      utils.lists.getUserListData.invalidate()
      setOpen(false)
      navigate(`/problems/lists/${data.list.id}`)
    },
    onError: (err) => {
      toast.error(err.message || 'Failed to save')
    },
  })

  const handleSubmit = useCallback(() => {
    const trimmed = name.trim()
    if (!trimmed) return
    forkMutation.mutate({ sourceType, sourceId, name: trimmed })
  }, [name, sourceType, sourceId, forkMutation])

  const handleOpenChange = useCallback((nextOpen: boolean) => {
    setOpen(nextOpen)
    if (!nextOpen) {
      setName('')
    }
  }, [])

  return (
    <>
      <Button
        size="icon-sm"
        variant="outline"
        className={className}
        onClick={() => {
          if (!isSignedIn) {
            openSignIn()
            return
          }
          setOpen(true)
        }}
        aria-label="Save"
      >
        <Star className="h-4 w-4 text-muted-foreground" />
      </Button>

      <Dialog open={open} onOpenChange={handleOpenChange}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>Save to a new list</DialogTitle>
            <DialogDescription>
              This will create a new list with all the problems from this{' '}
              {sourceType === 'list' ? 'list' : sourceType}.
            </DialogDescription>
          </DialogHeader>
          <div className="flex flex-col gap-4">
            <div>
              <label
                htmlFor="fork-list-name"
                className="mb-1 block text-sm font-medium"
              >
                Name
              </label>
              <div className="relative">
                <Input
                  id="fork-list-name"
                  value={name}
                  onChange={(e) => {
                    if (e.target.value.length <= 40) {
                      setName(e.target.value)
                    }
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault()
                      handleSubmit()
                    }
                  }}
                  placeholder="List name"
                  maxLength={40}
                  autoFocus
                />
                <span className="text-muted-foreground absolute right-2 top-1/2 -translate-y-1/2 text-xs">
                  {name.length}/40
                </span>
              </div>
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="ghost" onClick={() => handleOpenChange(false)}>
                Cancel
              </Button>
              <Button
                onClick={handleSubmit}
                disabled={
                  !name.trim() ||
                  forkMutation.isPending ||
                  defaultNameQuery.isLoading
                }
              >
                Save
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  )
}
